import React, { useEffect, useMemo, useRef } from 'react';

import { apiContainer } from '@vlabs/api-bindings';
import type { LunaClient } from '@vlabs/api-bindings';
import { showResourceIntensiveTaskPopup } from '@vlabs/shared/widgets/tasks/showResourceIntensiveTaskPopup';
import { Page, Stepper } from '@vlabs/uikit';
import cn from 'classnames';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import 'react-checkbox-tree/lib/react-checkbox-tree.css';
import { toast } from 'react-toastify';

import { selectAccountEmail, selectAccountLogin } from '@vlabs/pages/auth/selectors';

import { StepCallbacksPolicy } from '@vlabs/monokit/entities/handlers/ui/steps';
import { useSteppedForm } from '@vlabs/monokit/shared/lib/use-stepped-form/useSteppedForm';

import { stepByObjectType } from '../constants';
import { getDefaultValuesByObjectType } from '../defaultValues';
import st from './StepperModal.module.sass';
import { StepColumns } from './steps';
import type { TObjectType, TFilters, TTaskFormValues } from '../types';

import './ExportTaskForm.sass';

interface StepperModalProps {
  objectType: TObjectType;
  filters?: TFilters;
  onSubmit: VoidFunction;
}

export function ExportTaskStepper({
  filters = {},
  onSubmit,
  objectType,
}: StepperModalProps): React.JSX.Element {
  const { t } = useTranslation();

  const steps = [stepByObjectType[objectType], StepColumns, StepCallbacksPolicy];
  const email = useSelector(selectAccountLogin ?? selectAccountEmail);
  const defaultValues = useMemo(() => ({
    ...getDefaultValuesByObjectType(objectType, filters),
    description: email,
  }), [objectType, filters, email]);
  const form = useForm({ defaultValues, mode: 'onChange' });
  const prevForm = useRef(defaultValues);

  useEffect(() => {
    prevForm.current = defaultValues;
    form.reset(defaultValues);
  }, [form.reset, defaultValues, prevForm, form]);

  const stepper = useSteppedForm({ form, steps, onSubmit });

  const exportTaskRequest = async ({ meta = [], ...props }: TTaskFormValues) => {
    await (apiContainer.lunaClient as LunaClient).tasks.exporter({ meta, ...props });
    toast.info(t('features:exportTask.toasts.created'));
  };

  const onSubmitForm = (values: TTaskFormValues) => {
    const onConfirm = async () => {
      await exportTaskRequest(values);
      onSubmit?.();
    };

    showResourceIntensiveTaskPopup(onConfirm);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmitForm)}>
        <Page
          className={cn(st.StepperModalPage, 'ExportTaskForm')}
          title={t('features:exportTask.titles.export', { context: objectType }) as unknown as string}
        >
          <Stepper
            {...stepper}
            className={st.StepperModalContent}
          >
            {steps.map((Step) => <Step key={Step.name} />)}
          </Stepper>
        </Page>
      </form>
    </FormProvider>
  );
}
